import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown } from "lucide-react";

const TOTAL_FRAMES = 100;
const framePath = (i) => `/hero/frame_${String(i).padStart(3, "0")}.jpg?v=4`;
const WORK_TYPES = ["Product", "Media + Tech", "Research", "Built + Environment", "Art"];

export const HeroSequence = () => {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const frameNumsRef = useRef([]);
  const currentRef = useRef(-1);
  const rafRef = useRef(0);
  const textWrapRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const [autoIndex, setAutoIndex] = useState(0);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const step = isMobile ? 2 : 1;

  const draw = (idx) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = imagesRef.current[idx];
    if (!img || !img.complete || img.naturalWidth === 0) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
      canvas.width = Math.round(cw * dpr);
      canvas.height = Math.round(ch * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, cw, ch);
    const ir = img.naturalWidth / img.naturalHeight;
    const cr = cw / ch;
    let dw, dh, dx, dy;
    // "cover" — fill the whole hero edge-to-edge, cropping any overflow
    if (cr > ir) {
      dw = cw;
      dh = cw / ir;
      dx = 0;
      dy = (ch - dh) / 2;
    } else {
      dh = ch;
      dw = ch * ir;
      dx = (cw - dw) * (isMobile ? 0.8 : 0.5);
      dy = 0;
    }
    ctx.drawImage(img, dx, dy, dw, dh);
  };

  // Preload frames
  useEffect(() => {
    const nums = [];
    for (let i = 1; i <= TOTAL_FRAMES; i += step) nums.push(i);
    if (nums[nums.length - 1] !== TOTAL_FRAMES) nums.push(TOTAL_FRAMES);
    frameNumsRef.current = nums;

    let loadedCount = 0;
    const imgs = nums.map((n, idx) => {
      const img = new Image();
      img.src = framePath(n);
      img.onload = () => {
        loadedCount++;
        if (idx === 0) {
          setReady(true);
          draw(0);
        }
      };
      return img;
    });
    imagesRef.current = imgs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll → frame mapping, plus a gentle idle loop before the user scrolls
  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let scrollRaf = 0;
    let idleRaf = 0;
    let controlled = false; // true once scroll takes over
    const FPS = 9;
    let last = performance.now();
    let pos = 0;

    const countNow = () => frameNumsRef.current.length;
    const idleMax = () => Math.max(2, countNow() - 1);
    const isLoaded = (i) => {
      const im = imagesRef.current[i];
      return im && im.complete && im.naturalWidth > 0;
    };

    // gentle forward-only loop over the opening frames, only advancing to loaded frames
    const idleTick = (t) => {
      if (controlled) return;
      if (t - last >= 1000 / FPS) {
        last = t;
        const max = idleMax();
        let next = pos + 1;
        if (next > max) next = 0; // loop back to start, always moving forward
        if (isLoaded(next)) {
          pos = next;
          if (next !== currentRef.current) {
            currentRef.current = next;
            draw(next);
          }
        }
      }
      idleRaf = requestAnimationFrame(idleTick);
    };

    const onScroll = () => {
      const sec = sectionRef.current;
      if (!sec) return;
      const rect = sec.getBoundingClientRect();
      const total = sec.offsetHeight - window.innerHeight;
      const p = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
      setProgress(p);

      if (p > 0.004) {
        if (!controlled) {
          controlled = true;
          cancelAnimationFrame(idleRaf);
        }
        const c = countNow();
        const idx = Math.min(c - 1, Math.round(p * (c - 1)));
        if (idx !== currentRef.current) {
          currentRef.current = idx;
          cancelAnimationFrame(scrollRaf);
          scrollRaf = requestAnimationFrame(() => draw(idx));
        }
      } else if (!reduce && controlled) {
        // back at the very top → resume the idle loop
        controlled = false;
        pos = Math.min(idleMax(), Math.max(0, currentRef.current));
        last = performance.now();
        cancelAnimationFrame(idleRaf);
        idleRaf = requestAnimationFrame(idleTick);
      }
    };

    const onResize = () => draw(Math.max(0, currentRef.current));
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    if (!reduce) {
      idleRaf = requestAnimationFrame(idleTick);
    }
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(scrollRaf);
      cancelAnimationFrame(idleRaf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-cycle the work-type words while the visitor is still at the top.
  useEffect(() => {
    const id = setInterval(() => {
      setAutoIndex((i) => (i + 1) % WORK_TYPES.length);
    }, 1600);
    return () => clearInterval(id);
  }, []);

  // Subtle cursor parallax on the hero text (desktop only)
  useEffect(() => {
    if (isMobile) return;
    const onMove = (e) => {
      const nx = e.clientX / window.innerWidth - 0.5;
      const ny = e.clientY / window.innerHeight - 0.5;
      if (textWrapRef.current) {
        textWrapRef.current.style.transform = `translate3d(${nx * 12}px, ${ny * 9}px, 0)`;
      }
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const supportingOpacity = Math.min(1, Math.max(0, (progress - 0.12) / 0.18));

  // Work-type: auto-cycles on landing (before scroll), then follows scroll position.
  const workIndex = Math.min(
    WORK_TYPES.length - 1,
    Math.floor(progress * WORK_TYPES.length)
  );
  const scrolled = progress > 0.01;
  const displayIndex = scrolled ? workIndex : autoIndex;
  const currentWork = WORK_TYPES[displayIndex];

  return (
    <section
      ref={sectionRef}
      id="top"
      data-testid="hero-section"
      className="relative"
      style={{ height: isMobile ? "260vh" : "420vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-white">
        {/* Frame canvas */}
        <div className="hero-canvas-in absolute inset-0">
          <canvas
            ref={canvasRef}
            data-testid="hero-canvas"
            className="absolute inset-0 w-full h-full animate-breathe"
          />
        </div>

        {/* loading shimmer */}
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-sans text-xs tracking-[0.3em] uppercase text-[#6B8F71] animate-pulse">
              drawing the world…
            </span>
          </div>
        )}

        {/* soft corner vignette — deeper darkness behind the lower-left text so it stays legible over the artwork */}
        <div
          className="absolute bottom-0 left-0 w-full sm:w-[82%] h-[82%] sm:h-[86%] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at bottom left, rgba(12,12,12,0.9) 0%, rgba(12,12,12,0.62) 38%, rgba(12,12,12,0.22) 62%, rgba(12,12,12,0) 80%)",
          }}
        />

        {/* soft fade at the bottom edge so the hero blends into the white section below (no harsh line) */}
        <div className="absolute inset-x-0 bottom-0 h-20 sm:h-28 bg-gradient-to-b from-transparent to-white pointer-events-none" />

        {/* Hero text: tagline lower-left in the reading zone */}
        <div className="absolute inset-0">
          <div className="w-full h-full px-6 sm:px-10 lg:px-16 pt-24 sm:pt-28 pb-52 sm:pb-64 lg:pb-72 flex flex-col justify-end">
            {/* lower-left tagline — most prominent */}
            <div
              ref={textWrapRef}
              className="max-w-xl"
              style={{ transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)" }}
            >
              <motion.p
                data-testid="hero-welcome"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="font-sans font-bold text-sm sm:text-base tracking-[0.28em] uppercase text-[#C9A24B] mb-3 drop-shadow-[0_2px_14px_rgba(0,0,0,0.95)]"
              >
                Welcome to my world!
              </motion.p>
              <motion.p
                data-testid="hero-secondary"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.35)]"
              >
                I design systems into form.
              </motion.p>

              {/* scroll-driven work-type — toggles one-by-one as you scroll */}
              <div
                data-testid="hero-worktypes"
                className="mt-4 h-9 sm:h-10 flex items-center gap-3 max-w-md sm:max-w-lg"
              >
                <span className="font-sans font-bold text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C9A24B] drop-shadow-[0_2px_12px_rgba(0,0,0,0.95)]">
                  I work across
                </span>
                <span className="relative inline-flex items-center overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentWork}
                      data-testid="hero-worktype-active"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -16 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="font-serif italic text-xl sm:text-2xl text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.95)] whitespace-nowrap"
                    >
                      {currentWork}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </div>
              {/* progress ticks for the work-types */}
              <div className="mt-2 flex items-center gap-1.5" aria-hidden="true">
                {WORK_TYPES.map((w, i) => (
                  <span
                    key={w}
                    className="block h-0.5 rounded-full transition-all duration-300"
                    style={{
                      width: i === displayIndex ? 22 : 10,
                      backgroundColor: i === displayIndex ? "#C9A24B" : "rgba(255,255,255,0.4)",
                    }}
                  />
                ))}
              </div>
              <motion.p
                data-testid="hero-subtext"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
                className="font-sans font-light text-sm sm:text-base text-white/90 leading-relaxed mt-5 max-w-md drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]"
              >
                The instinct: Find pattern, give it shape.
              </motion.p>
              <motion.a
                href="#about"
                data-testid="hero-view-work"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
                className="mt-7 inline-flex items-center gap-2 font-sans text-sm tracking-wide px-6 py-3 rounded-full bg-white text-[#1A1A1A] hover:bg-[#C9A24B] hover:text-white transition-colors duration-300"
              >
                View thought process &amp; works
                <ArrowDown size={16} />
              </motion.a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSequence;
